import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  Card,
  Modal,
  Input,
  SearchInput,
} from '@/components/ui';
import { RefreshCw, Users, CheckCircle2, Clock, RotateCcw } from 'lucide-react';
import { parseLocalDateTime } from '@/lib/date';
import { useDebouncedValue } from '@/hooks';
import { AdminPreApproved } from './AdminPreApproved';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-users`;
const RESET_FN_URL = `${supabaseUrl}/functions/v1/admin-reset-user`;
const SET_TIER_FN_URL = `${supabaseUrl}/functions/v1/admin-set-tier`;

type Tier = 'FREE' | 'CORE' | 'FAMILY';
const TIERS: ReadonlyArray<Tier> = ['FREE', 'CORE', 'FAMILY'];

interface ResetSummary {
  user_id: string;
  email: string;
  supabase_id: string | null;
  households_deleted: number;
  emails_deleted: number;
  feedback_deleted: number;
  // Optional for backwards compatibility with pre-migration deployments
  // that haven't picked up the updated RPC yet.
  pre_approved_rows_cleared?: number;
  // Same: added with the YAR-471 migration.
  sessions_revoked?: number;
  user_row_deleted: boolean;
}

interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  approved: boolean;
  created_at: string;
  /**
   * The household this user owns as PRIMARY, or null when they only
   * appear as a VIEWER. Tiers are per-household, so the change-tier
   * action is only meaningful for users with a PRIMARY household.
   */
  primary_household_id: string | null;
  /**
   * Current subscription tier of `primary_household_id`. null when the
   * user has no PRIMARY household; 'FREE' is the default for households
   * without an explicit subscription row.
   */
  current_tier: Tier | null;
}

// Admin audit formatter. Reuses @/lib/date's parseLocalDateTime so naive /
// offset-bearing timestamps render consistently with the rest of the app.
function formatDate(iso: string): string {
  return parseLocalDateTime(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface AdminUsersProps {
  password: string;
}

export function AdminUsers({ password }: AdminUsersProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Free-text filter over the loaded user list. `query` updates on every
  // keystroke (responsive input); the debounced copy drives the actual
  // filtering so we re-render the table at most once per pause in typing.
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 200);
  // Reset modal: when set, the modal is open targeting that user.
  const [userToReset, setUserToReset] = useState<AdminUser | null>(null);
  const [resetConfirmInput, setResetConfirmInput] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  // Tier-change modal — when set, the modal is open targeting that user.
  const [userToChangeTier, setUserToChangeTier] = useState<AdminUser | null>(
    null,
  );
  const [tierTarget, setTierTarget] = useState<Tier>('FREE');
  const [tierReason, setTierReason] = useState('');
  const [tierSubmitting, setTierSubmitting] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);
  const [tierNotice, setTierNotice] = useState<string | null>(null);

  const pendingCount = useMemo(
    () => users.filter((u) => !u.approved).length,
    [users],
  );

  // Case-insensitive match on email + display name. Everything is loaded
  // client-side already (admin lists are small), so filtering in-memory is
  // instant and avoids round-trips to the edge function.
  const normalizedQuery = debouncedQuery.trim().toLowerCase();
  const isFiltering = normalizedQuery.length > 0;
  const visibleUsers = useMemo(() => {
    if (!normalizedQuery) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(normalizedQuery) ||
        (u.display_name?.toLowerCase().includes(normalizedQuery) ?? false),
    );
  }, [users, normalizedQuery]);

  const fetchUsers = useCallback(async (pw: string) => {
    setLoading(true);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': pw },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unauthorized' }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data: AdminUser[] = await res.json();
      setUsers(data);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(password);
  }, [password, fetchUsers]);

  const toggleApproval = async (userId: string, approved: boolean) => {
    setActionLoading(true);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'PATCH',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_ids: [userId], approved }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, approved } : u)),
      );
    } catch (err) {
      console.error('Toggle failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const bulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'PATCH',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_ids: Array.from(selectedIds),
          approved: true,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setUsers((prev) =>
        prev.map((u) => (selectedIds.has(u.id) ? { ...u, approved: true } : u)),
      );
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk approve failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openResetModal = (user: AdminUser) => {
    setUserToReset(user);
    setResetConfirmInput('');
    setResetError(null);
    setResetNotice(null);
  };

  const closeResetModal = () => {
    if (resetSubmitting) return; // don't close mid-call
    setUserToReset(null);
    setResetConfirmInput('');
    setResetError(null);
  };

  // The destructive button only enables when the admin retypes the exact
  // stored email (case-insensitive — matches the RPC's defense).
  const resetMatches =
    userToReset !== null &&
    resetConfirmInput.trim().toLowerCase() === userToReset.email.toLowerCase();

  const performReset = async () => {
    if (!userToReset || !resetMatches) return;
    setResetSubmitting(true);
    setResetError(null);
    try {
      const res = await fetch(RESET_FN_URL, {
        method: 'POST',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userToReset.id,
          confirm_email: resetConfirmInput.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { ok: boolean; summary: ResetSummary };
      // The reset deleted the user row + their owned households; refetch so
      // the table reflects the new shape. The supabase_id is preserved on
      // auth.users so this user can sign in again — flagging it in the
      // notice gives the admin something to tell them.
      setUserToReset(null);
      setResetConfirmInput('');
      const nukeParts = [
        `${data.summary.households_deleted} household(s)`,
        `${data.summary.emails_deleted} email(s)`,
        `${data.summary.feedback_deleted} feedback row(s)`,
      ];
      if (data.summary.pre_approved_rows_cleared) {
        nukeParts.push(
          `${data.summary.pre_approved_rows_cleared} pre-approval row(s)`,
        );
      }
      if (data.summary.sessions_revoked) {
        nukeParts.push(`${data.summary.sessions_revoked} signed-in session(s)`);
      }
      setResetNotice(
        `Reset complete for ${data.summary.email} — ${nukeParts.join(', ')} nuked. Every device is signed out; they can sign in again and will land at the start of onboarding.`,
      );
      await fetchUsers(password);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setResetSubmitting(false);
    }
  };

  // ── Tier change ────────────────────────────────────────────────────
  const openTierModal = (user: AdminUser) => {
    setUserToChangeTier(user);
    setTierTarget((user.current_tier ?? 'FREE') as Tier);
    setTierReason('');
    setTierError(null);
    setTierNotice(null);
  };

  const closeTierModal = () => {
    if (tierSubmitting) return;
    setUserToChangeTier(null);
    setTierReason('');
    setTierError(null);
  };

  const performTierChange = async () => {
    if (!userToChangeTier?.primary_household_id) return;
    setTierSubmitting(true);
    setTierError(null);
    try {
      const res = await fetch(SET_TIER_FN_URL, {
        method: 'POST',
        headers: {
          'X-Admin-Password': password,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          household_id: userToChangeTier.primary_household_id,
          tier: tierTarget,
          reason: tierReason.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // Optimistic local update so the table reflects the new tier
      // without re-fetching everything. The server response is the
      // source of truth — surface the previous tier in the notice so
      // the admin has a paper-trail to undo if they fat-fingered it.
      const payload = (await res.json()) as {
        previous_tier: Tier | null;
        new_tier: Tier;
        reset_started_at: boolean;
      };
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userToChangeTier.id
            ? { ...u, current_tier: payload.new_tier }
            : u,
        ),
      );
      setUserToChangeTier(null);
      setTierReason('');
      setTierNotice(
        `Tier for ${userToChangeTier.email} changed: ${
          payload.previous_tier ?? '—'
        } → ${payload.new_tier}${
          payload.reset_started_at ? ' (usage counter reset)' : ''
        }.`,
      );
    } catch (err) {
      setTierError(err instanceof Error ? err.message : 'Tier change failed');
    } finally {
      setTierSubmitting(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select-all operates on the *visible* (filtered) users so the action
  // matches what the admin can actually see. Selections outside the current
  // filter are preserved — narrowing the search never silently drops them.
  const allSelected =
    visibleUsers.length > 0 && visibleUsers.every((u) => selectedIds.has(u.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleUsers.forEach((u) => next.delete(u.id));
      } else {
        visibleUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {/*
        Pre-approval lives in the same tab as Users because it's the same
        mental model — controlling who gets in. The compact card stays
        visible above the user list; a retro-approve from inside it
        triggers fetchUsers via onConsumed so the table below stays in
        sync without forcing the admin to hit Refresh.
      */}
      <AdminPreApproved
        password={password}
        onConsumed={() => void fetchUsers(password)}
      />

      {/* Header row: counts + action buttons */}
      <section className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {/* Counts */}
        <div className="flex items-center gap-2">
          <Badge
            variant="info"
            size="sm"
          >
            <Users
              size={12}
              className="mr-1"
            />
            {isFiltering
              ? `${visibleUsers.length} of ${users.length} users`
              : `${users.length} users`}
          </Badge>
          {pendingCount > 0 && (
            <Badge
              variant="warning"
              size="sm"
            >
              <Clock
                size={12}
                className="mr-1"
              />
              {pendingCount} pending
            </Badge>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              onClick={bulkApprove}
              loading={actionLoading}
              size="sm"
              leftIcon={<CheckCircle2 size={14} />}
            >
              Approve Selected ({selectedIds.size})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchUsers(password)}
            loading={loading}
            leftIcon={<RefreshCw size={14} />}
          >
            Refresh
          </Button>
        </div>
      </section>

      {/* Search filter — only meaningful once at least one user has loaded.
          Stays mounted while filtering (even with zero matches) so the admin
          can always clear or refine the query. */}
      {users.length > 0 ? (
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or name…"
          aria-label="Search users"
          className="w-full md:max-w-xs"
        />
      ) : null}

      {/* User list */}
      <section>
        {loading && users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No users found.
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No users match “{debouncedQuery.trim()}”.
          </div>
        ) : (
          <>
            {/* Select all / deselect all bar */}
            <div className="flex items-center gap-3 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
              {selectedIds.size > 0 && (
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selected
                </span>
              )}
            </div>

            {/* Mobile card layout (default) */}
            <div className="flex flex-col gap-3 md:hidden">
              {visibleUsers.map((user) => (
                <Card
                  key={user.id}
                  variant="default"
                  padding="none"
                  className={[
                    'transition-colors duration-150',
                    selectedIds.has(user.id)
                      ? 'border-primary bg-primary-light'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="flex items-start gap-3 p-4">
                    {/* Checkbox */}
                    <div className="pt-0.5">
                      <Checkbox
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                      />
                    </div>

                    {/* Card body */}
                    <div className="flex-1 min-w-0">
                      {/* Email (primary) + status */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user.email}
                        </p>
                        <Badge
                          variant={user.approved ? 'success' : 'warning'}
                          size="sm"
                        >
                          {user.approved ? 'Approved' : 'Pending'}
                        </Badge>
                      </div>

                      {/* Display name */}
                      <p className="text-sm text-muted-foreground mt-1">
                        {user.display_name ?? '--'}
                      </p>

                      {/* Tier row (PRIMARY only) */}
                      {user.primary_household_id ? (
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <Badge
                            variant={
                              user.current_tier === 'FAMILY'
                                ? 'success'
                                : user.current_tier === 'CORE'
                                  ? 'info'
                                  : 'default'
                            }
                            size="sm"
                          >
                            {user.current_tier ?? 'FREE'}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() => openTierModal(user)}
                            aria-label={`Change tier for ${user.email}`}
                          >
                            Change tier
                          </Button>
                        </div>
                      ) : null}

                      {/* Date + action */}
                      <div className="flex items-center justify-between mt-3 gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(user.created_at)}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionLoading}
                            leftIcon={<RotateCcw size={12} />}
                            onClick={() => openResetModal(user)}
                            aria-label={`Reset ${user.email}`}
                          >
                            Reset
                          </Button>
                          <Button
                            variant={user.approved ? 'outline' : 'primary'}
                            size="sm"
                            disabled={actionLoading}
                            onClick={() =>
                              toggleApproval(user.id, !user.approved)
                            }
                          >
                            {user.approved ? 'Revoke' : 'Approve'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Desktop table layout (md and up) */}
            <div className="hidden md:block">
              <Card
                variant="default"
                padding="none"
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="p-4 pr-2 w-10">
                        <Checkbox
                          checked={allSelected}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th className="p-4 font-medium">Email</th>
                      <th className="p-4 font-medium">Name</th>
                      <th className="p-4 font-medium">Created</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Tier</th>
                      <th className="p-4 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUsers.map((user) => (
                      <tr
                        key={user.id}
                        className={[
                          'border-b border-border last:border-b-0 transition-colors duration-150',
                          selectedIds.has(user.id) ? 'bg-primary-light' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <td className="p-4 pr-2">
                          <Checkbox
                            checked={selectedIds.has(user.id)}
                            onChange={() => toggleSelect(user.id)}
                          />
                        </td>
                        <td className="p-4 font-medium text-foreground">
                          {user.email}
                        </td>
                        <td className="p-4 text-muted-foreground">
                          {user.display_name ?? '--'}
                        </td>
                        <td className="p-4 text-muted-foreground whitespace-nowrap">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="p-4">
                          <Badge
                            variant={user.approved ? 'success' : 'warning'}
                          >
                            {user.approved ? 'Approved' : 'Pending'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {user.primary_household_id ? (
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  user.current_tier === 'FAMILY'
                                    ? 'success'
                                    : user.current_tier === 'CORE'
                                      ? 'info'
                                      : 'default'
                                }
                                size="sm"
                              >
                                {user.current_tier ?? 'FREE'}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={actionLoading}
                                onClick={() => openTierModal(user)}
                                aria-label={`Change tier for ${user.email}`}
                              >
                                Change
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              — (caregiver)
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant={user.approved ? 'outline' : 'primary'}
                              size="sm"
                              disabled={actionLoading}
                              onClick={() =>
                                toggleApproval(user.id, !user.approved)
                              }
                            >
                              {user.approved ? 'Revoke' : 'Approve'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={actionLoading}
                              leftIcon={<RotateCcw size={12} />}
                              onClick={() => openResetModal(user)}
                              aria-label={`Reset ${user.email}`}
                            >
                              Reset
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </>
        )}
      </section>

      {/* Reset notice — shows after a successful reset until dismissed or
          superseded by another action. Lives outside the conditional list
          so the empty state doesn't hide the confirmation. */}
      {resetNotice ? (
        <div
          role="status"
          className="px-4 py-3 bg-success-light border border-success rounded-md text-success text-sm"
        >
          {resetNotice}
        </div>
      ) : null}

      {/* Tier-change notice — same pattern as reset, narrower copy. */}
      {tierNotice ? (
        <div
          role="status"
          className="px-4 py-3 bg-success-light border border-success rounded-md text-success text-sm"
        >
          {tierNotice}
        </div>
      ) : null}

      {/* Reset confirmation modal. Typed-email gate matches the RPC's
          email_mismatch defense — both layers must agree before any DB
          writes happen. */}
      <Modal
        open={userToReset !== null}
        onClose={closeResetModal}
        title="Reset this user?"
      >
        {userToReset ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 text-sm text-foreground">
              <p>
                This permanently deletes <strong>{userToReset.email}</strong>
                's app state so they can re-run onboarding from scratch with the
                same Google account.
              </p>
              <ul className="list-disc pl-5 text-muted-foreground text-xs flex flex-col gap-1">
                <li>
                  Their owned household is deleted (children, inbox address,
                  Gmail connection, approved senders, emails, tasks, events,
                  notifications — everything).
                </li>
                <li>
                  Any caregivers who only had access via that household lose it.
                  They'll need a fresh invitation.
                </li>
                <li>
                  Their Google sign-in still works. On next sign-in they land at
                  the start of onboarding.
                </li>
              </ul>
              <p className="text-xs text-muted-foreground">
                Tell the user to sign out + back in if they're currently logged
                in — their JWT is stale until they refresh.
              </p>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Type the user's email to confirm:
              </span>
              <Input
                type="email"
                placeholder={userToReset.email}
                value={resetConfirmInput}
                onChange={(e) => setResetConfirmInput(e.target.value)}
                disabled={resetSubmitting}
                aria-label="Confirm user email"
              />
            </label>

            {resetError ? (
              <div
                role="alert"
                className="px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-xs"
              >
                {resetError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeResetModal}
                disabled={resetSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void performReset()}
                disabled={!resetMatches || resetSubmitting}
                loading={resetSubmitting}
                leftIcon={<RotateCcw size={14} />}
              >
                Reset user
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Tier-change modal. Manual override that bypasses Stripe —
          appropriate for comping users during beta. The endpoint nulls
          out price_id so the row is clearly distinguishable from a
          paid subscription on reconciliation. */}
      <Modal
        open={userToChangeTier !== null}
        onClose={closeTierModal}
        title="Change subscription tier"
      >
        {userToChangeTier ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 text-sm text-foreground">
              <p>
                Overrides the subscription tier for{' '}
                <strong>{userToChangeTier.email}</strong>'s household.
              </p>
              <ul className="list-disc pl-5 text-muted-foreground text-xs flex flex-col gap-1">
                <li>
                  No Stripe charge is created — this is a manual override.
                </li>
                <li>
                  Upgrading resets the monthly usage counter so the user starts
                  fresh on the new tier.
                </li>
                <li>
                  If the user later checks out via Stripe, the webhook will
                  overwrite this override with the paid tier.
                </li>
              </ul>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Current tier: {userToChangeTier.current_tier ?? 'FREE'}
              </span>
              <span className="text-xs text-muted-foreground">New tier:</span>
              <select
                value={tierTarget}
                onChange={(e) => setTierTarget(e.target.value as Tier)}
                disabled={tierSubmitting}
                className="rounded-sm border border-border-strong bg-background px-2 py-1.5 text-base text-foreground"
                aria-label="New tier"
              >
                {TIERS.map((t) => (
                  <option
                    key={t}
                    value={t}
                  >
                    {t}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Reason (optional, captured in the audit log):
              </span>
              <Input
                type="text"
                placeholder="e.g. comped for beta feedback"
                value={tierReason}
                onChange={(e) => setTierReason(e.target.value)}
                disabled={tierSubmitting}
                aria-label="Reason for tier change"
              />
            </label>

            {tierError ? (
              <div
                role="alert"
                className="px-3 py-2 bg-danger-light border border-danger rounded-md text-danger text-xs"
              >
                {tierError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeTierModal}
                disabled={tierSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void performTierChange()}
                disabled={
                  tierSubmitting ||
                  tierTarget === (userToChangeTier.current_tier ?? 'FREE')
                }
                loading={tierSubmitting}
              >
                Set tier
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
